import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Clock, ShieldCheck, ShieldAlert, Timer, Award, TrendingUp } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend, Cell,
} from "recharts";

const BUCKET_COLORS = { "<24h": "#059669", "24-72h": "#0284C7", "3-7d": "#D97706", ">7d": "#DC2626" };

export default function ApprovalSLA() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reports/approval-sla").then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="p-8 text-gray-500">Loading approval SLA…</div>;

  const k = data.kpis;
  const monthly = data.monthly.map(m => ({ ...m, month: m.month.slice(2) }));

  return (
    <div className="p-6 md:p-8" data-testid="approval-sla-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">Governance</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Approval SLA</h1>
        <p className="text-sm text-gray-500 mt-1">Time-in-queue, throughput, and approver performance across the certificate approval pipeline.</p>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi label="Pending" value={k.pending} icon={Clock} accent="bg-[#F59E0B]" testid="sla-kpi-pending" />
        <Kpi label="Approval Rate" value={`${k.approval_rate_pct}%`} icon={Award} accent="bg-[#01ADEF]" testid="sla-kpi-rate" />
        <Kpi label="Avg Time to Approve" value={fmtHours(k.avg_approval_hours)} icon={Timer} accent="bg-[#059669]" testid="sla-kpi-avg-approve" />
        <Kpi label="Oldest Pending" value={fmtHours(k.oldest_pending_hours)} icon={TrendingUp} accent="bg-[#DC2626]" testid="sla-kpi-oldest" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <MiniStat label="Approved (all-time)" value={k.approved} icon={ShieldCheck} tone="text-[#059669]" testid="sla-mini-approved" />
        <MiniStat label="Rejected (all-time)" value={k.rejected} icon={ShieldAlert} tone="text-[#DC2626]" testid="sla-mini-rejected" />
        <MiniStat label="Avg Time to Reject" value={fmtHours(k.avg_rejection_hours)} icon={Timer} tone="text-gray-700" testid="sla-mini-avg-reject" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* Monthly throughput */}
        <div className="bg-white border border-gray-200 p-5">
          <div className="mb-4">
            <h2 className="font-display text-lg font-bold tracking-tight">Monthly Approval Throughput</h2>
            <p className="text-xs text-gray-500 mt-0.5">Certificates issued vs. approved vs. rejected (last 6 months)</p>
          </div>
          {monthly.length === 0 ? (
            <div className="text-center py-16 text-sm text-gray-500">No historical data yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="month" tick={{ fill: "#6B7280", fontSize: 12 }} />
                <YAxis tick={{ fill: "#6B7280", fontSize: 12 }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #D1D5DB" }} />
                <Legend />
                <Line type="monotone" dataKey="issued" stroke="#01ADEF" strokeWidth={2} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="approved" stroke="#059669" strokeWidth={2} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="rejected" stroke="#DC2626" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Backlog age buckets */}
        <div className="bg-white border border-gray-200 p-5">
          <div className="mb-4">
            <h2 className="font-display text-lg font-bold tracking-tight">Pending Backlog by Age</h2>
            <p className="text-xs text-gray-500 mt-0.5">How long pending certs have been waiting for approval</p>
          </div>
          {data.backlog_buckets.every(b => b.count === 0) ? (
            <div className="text-center py-16 text-sm text-gray-500">Nothing pending — you&apos;re all caught up.</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.backlog_buckets}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="bucket" tick={{ fill: "#6B7280", fontSize: 12 }} />
                <YAxis tick={{ fill: "#6B7280", fontSize: 12 }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #D1D5DB" }} />
                <Bar dataKey="count">
                  {data.backlog_buckets.map((b) => <Cell key={`bkt-${b.bucket}`} fill={BUCKET_COLORS[b.bucket] || "#6B7280"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Per-approver leaderboard */}
      <div className="bg-white border border-gray-200 overflow-x-auto">
        <div className="px-5 py-4 border-b border-gray-200">
          <h2 className="font-display text-lg font-bold tracking-tight">Approver Leaderboard</h2>
          <p className="text-xs text-gray-500 mt-0.5">Volume &amp; average turnaround per approver</p>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="border-b border-gray-200 bg-gray-50"><Th>Rank</Th><Th>Approver</Th><Th>Approved</Th><Th>Avg Hours</Th></tr></thead>
          <tbody data-testid="approver-leaderboard">
            {data.per_approver.length === 0 ? (
              <tr><td colSpan={4} className="text-center py-10 text-gray-500">No approvals recorded yet.</td></tr>
            ) : data.per_approver.map((a, idx) => (
              <tr key={a.approver_id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`approver-row-${a.approver_id}`}>
                <Td className="font-mono">#{idx + 1}</Td>
                <Td className="font-semibold">{a.approver_name}</Td>
                <Td className="font-mono">{a.approved}</Td>
                <Td className="font-mono">{fmtHours(a.avg_hours)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function fmtHours(h) {
  if (h == null) return "—";
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 24) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
}

const Kpi = ({ label, value, icon: Icon, accent, testid }) => (
  <div className="bg-white border border-gray-200 p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs uppercase tracking-wider font-semibold text-gray-500">{label}</span>
      <div className={`w-8 h-8 flex items-center justify-center ${accent}`}><Icon className="w-4 h-4 text-white" strokeWidth={2.5} /></div>
    </div>
    <div className="font-display text-4xl font-bold tracking-tight">{value}</div>
  </div>
);
const MiniStat = ({ label, value, icon: Icon, tone, testid }) => (
  <div className="bg-white border border-gray-200 p-4 flex items-center gap-3" data-testid={testid}>
    <Icon className={`w-5 h-5 ${tone}`} strokeWidth={2} />
    <div>
      <div className="text-xs uppercase tracking-wider font-semibold text-gray-500">{label}</div>
      <div className="font-display text-2xl font-bold tracking-tight">{value}</div>
    </div>
  </div>
);
const Th = ({ children }) => <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold text-gray-600">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
